import { findSubjectByPlatform_findSubjectByPlatform_tabs as SubjectType } from '__generatedTypes__/findSubjectByPlatform'
import { FaqPages, QuestionTypeEnum, ClassroomTabEnum } from '__generatedTypes__/globalTypes'
import { CourseName } from 'theme/utils'
import { cmsRoutes } from 'routes/cmsRoutes'
import {
  CasperBoosterBaseSubject,
  INBDEBoosterBaseSubject,
  NBDHEBoosterBaseSubject,
  PlatformSlug,
} from 'constants/consts'
import { DescriptionPages } from 'components/DescriptionForm'
import { SubjectExtended, SUBJECT_TABS_CONFIG } from 'hooks/usePlatformsAndSubjects'

export const QB_SUBJECTS_WITHOUT_CATEGORIES: CourseName[] = [
  CourseName.Anatomy,
  CourseName.Pharmacology,
  CourseName.EthicsBehavior,
  CourseName.FieldsOfDentistry,
  CourseName.PatientManagement,
  CourseName.DiagnosisDisorders,
  CourseName.Research,
  CourseName.Keyholes,
  CourseName.TopFrontEnd,
  CourseName.AngleRanking,
  CourseName.HolePunching,
  CourseName.CubeCounting,
  CourseName.PatternFolding,
  CourseName.Ethics,
  CourseName.Periodontics,
  CourseName.PathologyRadiology,
  CourseName.OralMedicineAndPathology,
  CourseName.ScientificBasis,
  CourseName.CommunityHealth,
  CourseName.Endodontics,
  CourseName.OralSurgery,
  CourseName.Prosthodontics,
  CourseName.Orthodontics,
  CourseName.Operative,
  CourseName.MedicalEmergencies,
  CourseName.Prescriptions,
  CourseName.Optics,
  CourseName.OcularPathology,
  CourseName.VisualFunction,
  CourseName.SystemicHealth,
  CourseName.AnatomyPhysiology,
]

export const CASE_SCENARIO_SUBJECT_IDS: Partial<Record<PlatformSlug, string>> = {
  [PlatformSlug.INBDEBooster]: INBDEBoosterBaseSubject,
  [PlatformSlug.NBDHEBooster]: NBDHEBoosterBaseSubject,
  [PlatformSlug.CasperBooster]: CasperBoosterBaseSubject,
  // TODO: add for PlatformSlug.NDEBCrusher
}

// CBS:
export const CASE_BASED_SCENARIOS_TAB_INDEX = 1 // INBDE, NBDHE
export const COMMON_QB_TAB_INDEX = 3 // Casper

// QB Classroom Tabs:
// Default Question Bank Tab has index 1
export const REACTION_QB_TAB_INDEX = 2 // DAT
export const OSCE_STANDALONE_QB_TAB_INDEX = 11 // NDEB
export const OSCE_MULTI_SELECT_QB_TAB_INDEX = 12 // NDEB
export const OSCE_RX_WRITING_QB_TAB_INDEX = 13 // NDEB
export const COMMUNITY_TESTLETS_TAB_INDEX = 14 // NBDHE

export const getQBTitle = (isCaseBasedScenario?: boolean, tabIndex?: number, isMany?: boolean) => {
  const cbsTitles: Record<number, string> = {
    [CASE_BASED_SCENARIOS_TAB_INDEX]: 'Case-Based Scenario',
    [COMMON_QB_TAB_INDEX]: 'Question Bank',
  }

  const titles: Record<number, string> = {
    0: 'Question Bank',
    [OSCE_STANDALONE_QB_TAB_INDEX]: 'Standalone QBank',
    [OSCE_MULTI_SELECT_QB_TAB_INDEX]: 'Multiselect QBank',
    [OSCE_RX_WRITING_QB_TAB_INDEX]: 'Rx. Writing QBank',
    [COMMUNITY_TESTLETS_TAB_INDEX]: 'Community Testlet',
    ...(isCaseBasedScenario ? cbsTitles : {}),
  }

  return (titles[tabIndex ?? 0] ?? titles[0]) + (isMany ? 's' : '')
}

export const getCBSDashboardLink = (tabIndex?: number) => {
  const links: Record<number, string> = {
    [CASE_BASED_SCENARIOS_TAB_INDEX]: cmsRoutes.CASE_BASED_SCENARIO_LIST,
    [COMMON_QB_TAB_INDEX]: cmsRoutes.SCENARIOS_QB_LIST,
  }

  return links[tabIndex ?? 1]
}

export const getQBDashboardLink = (tabIndex?: number) => {
  const links: Record<number, string> = {
    0: cmsRoutes.QUESTION_BANK_LIST,
    [OSCE_STANDALONE_QB_TAB_INDEX]: cmsRoutes.OSCE_STANDALON_QB_LIST,
    [OSCE_MULTI_SELECT_QB_TAB_INDEX]: cmsRoutes.OSCE_MULTISELECT_QB_LIST,
    [OSCE_RX_WRITING_QB_TAB_INDEX]: cmsRoutes.OSCE_RX_WRITING_QB_LIST,
    [COMMUNITY_TESTLETS_TAB_INDEX]: cmsRoutes.COMMUNITY_TESTLETS_LIST,
  }

  return links[tabIndex ?? 0] ?? links[0]
}

export const getCBSDescriptionPage = (tabIndex?: number) => {
  const pages: Record<number, DescriptionPages> = {
    [CASE_BASED_SCENARIOS_TAB_INDEX]: DescriptionPages.CaseBasedScenarios,
    [COMMON_QB_TAB_INDEX]: DescriptionPages.CaseBasedScenarios, // we reuse the same
  }

  return pages[tabIndex ?? 1]
}

export const getCBSFaqPage = (tabIndex?: number) => {
  const pages: Record<number, FaqPages> = {
    [CASE_BASED_SCENARIOS_TAB_INDEX]: FaqPages.CaseBasedScenario,
    [COMMON_QB_TAB_INDEX]: FaqPages.CaseBasedScenario, // we reuse the same
  }

  return pages[tabIndex ?? 1]
}

type Subject = Pick<SubjectType, 'id' | 'tabName' | 'slug'>

export const isCaseScenarioPlatform = (platformSlug?: PlatformSlug | null) =>
  !!platformSlug && Object.keys(CASE_SCENARIO_SUBJECT_IDS).includes(platformSlug)

export const isCaseScenarioSubject = (subject?: Subject | null) =>
  !!subject && Object.values(CASE_SCENARIO_SUBJECT_IDS).includes(subject.id)

export const isSubjectWithoutQBCategory = (subject?: Subject | null) => {
  if (!subject) {
    return false
  }

  if (QB_SUBJECTS_WITHOUT_CATEGORIES.includes(subject.tabName as CourseName)) {
    return true
  }

  if (isCaseScenarioSubject(subject)) {
    return true
  }

  return false
}

export type AvailableQuestionTypesArgs = {
  tabIndex?: number
  platformSlug?: PlatformSlug
}

export const getAvailableQuestionTypes = (args?: AvailableQuestionTypesArgs) => {
  const { tabIndex, platformSlug } = args ?? {}

  if (tabIndex === OSCE_STANDALONE_QB_TAB_INDEX) {
    return [QuestionTypeEnum.SingleChoiceNDEB]
  }
  if (tabIndex === OSCE_MULTI_SELECT_QB_TAB_INDEX) {
    return [QuestionTypeEnum.MultiChoiceNDEB]
  }
  if (tabIndex === OSCE_RX_WRITING_QB_TAB_INDEX) {
    return [QuestionTypeEnum.RxWritingNDEB]
  }

  const baseQuestionTypes = [
    QuestionTypeEnum.SingleChoice,
    QuestionTypeEnum.SingleChoiceInbde,
    QuestionTypeEnum.CaseNBDHE,
  ]

  if (platformSlug === PlatformSlug.NBDHEBooster) {
    return [...baseQuestionTypes, QuestionTypeEnum.TestletNBDHE]
  }

  if (platformSlug === PlatformSlug.DLOSCE) {
    return [QuestionTypeEnum.SingleChoiceDLOSCE, QuestionTypeEnum.MultiChoiceDLOSCE]
  }

  if (platformSlug === PlatformSlug.NBEOPartOne) {
    return [QuestionTypeEnum.SingleChoiceNBEO, QuestionTypeEnum.MultiChoiceNBEO]
  }

  if (platformSlug === PlatformSlug.GREBooster) {
    return [
      QuestionTypeEnum.SingleChoiceGRE,
      QuestionTypeEnum.MultiChoiceGre,
      QuestionTypeEnum.PassageSingleChoiceGRE,
      QuestionTypeEnum.PassageMultiChoiceGRE,
      QuestionTypeEnum.SentenceEquivalence,
      QuestionTypeEnum.NumericResponse,
      QuestionTypeEnum.TextCompletion,
    ]
  }

  // exclude QuestionTypeEnum.SingleChoiceCARS type, because these question are used as part of QuestionPassage
  return baseQuestionTypes
}

export const isReadingSubject = (slug: string): boolean => ['reading-comprehension'].includes(slug)

export const getQBClassroomTab = (subject: SubjectExtended | null): ClassroomTabEnum => {
  if (!subject) {
    return ClassroomTabEnum.QuestionBanks
  }

  if (isReadingSubject(subject.slug)) {
    return ClassroomTabEnum.ReadingQuestionBanks
  }

  // Check if subject is from SUBJECT_TABS_CONFIG and has tabIndex 2 (e.g., Organic Chemistry reaction QBank)
  if (SUBJECT_TABS_CONFIG[subject.slug]?.count === 2 && subject.tabIndex === REACTION_QB_TAB_INDEX) {
    return ClassroomTabEnum.ReactionQbank
  }

  switch (subject.tabIndex) {
    case OSCE_STANDALONE_QB_TAB_INDEX:
      return ClassroomTabEnum.StandaloneOSCEQBanks
    case OSCE_MULTI_SELECT_QB_TAB_INDEX:
      return ClassroomTabEnum.MultiselectOSCEQBanks
    case OSCE_RX_WRITING_QB_TAB_INDEX:
      return ClassroomTabEnum.RxWritingOSCEQBanks
    case COMMUNITY_TESTLETS_TAB_INDEX:
      return ClassroomTabEnum.CommunityTestlets
  }

  return ClassroomTabEnum.QuestionBanks
}
